import { Alert, Stack, Typography } from "@mui/material";
import type { Control } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { FormAccordionSection } from "Components/Shared/FormAccordionSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { FacilityFormModel } from "Components/Shared/Merchant/Facilities/schema";
import type { HunterTerminalDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { TerminalSelectionPanel } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/ProductActivationForm/TerminalSelectionPanel";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;
const TERMINAL_RESOURCES =
  Resources.Components.Merchant.OrbitL.TerminalSelection;
const FACILITY_TERMINAL_RESOURCES =
  Resources.Components.Merchant.OrbitL.FacilityTerminals;

type Props = {
  control: Control<FacilityFormModel>;
  isExpanded: boolean;
  onToggle: () => void;
  onNext: () => void;
  isComplete?: boolean;
  hasErrors: boolean;
  isHunterFinanced: boolean;
  isTerminalCountVisible: boolean;
  isSoftPosCountVisible: boolean;
  allTerminals: Array<HunterTerminalDto>;
  assignedTerminalIDs: ReadonlyArray<string>;
  totalRequiredCount: number;
  totalAvailableStock: number;
  isLoadingTerminals: boolean;
  isTerminalsError: boolean;
  selectedTerminalIDs: Array<string>;
  onSelectionChange: (ids: Array<string>) => void;
};

export const TerminalSection = ({
  control,
  isExpanded,
  onToggle,
  onNext,
  isComplete,
  hasErrors,
  isHunterFinanced,
  isTerminalCountVisible,
  isSoftPosCountVisible,
  allTerminals,
  assignedTerminalIDs,
  totalRequiredCount,
  totalAvailableStock,
  isLoadingTerminals,
  isTerminalsError,
  selectedTerminalIDs,
  onSelectionChange,
}: Props) => {
  const { t } = useResource();

  const terminalCount = useWatch({ control, name: "terminalCount" });
  const requiredCount = terminalCount ?? 0;

  const isStockExceeded =
    isHunterFinanced && totalRequiredCount > totalAvailableStock;
  const isTerminalSelectionVisible =
    isHunterFinanced && requiredCount > 0 && allTerminals.length > 0;

  return (
    <FormAccordionSection
      legend={t(RESOURCES.Legend.Terminals)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      onNext={onNext}
      isComplete={isComplete}
      hasErrors={hasErrors}
    >
      <Stack gap={2}>
        {isTerminalCountVisible && (
          <FormInput
            control={control}
            name="terminalCount"
            label={t(RESOURCES.Fields.TerminalCount)}
            helperText={t(RESOURCES.Fields.TerminalCountHelperText)}
            type="number"
            min={0}
          />
        )}
        {isSoftPosCountVisible && (
          <FormInput
            control={control}
            name="softPosCount"
            label={t(RESOURCES.Fields.SoftPosCount)}
            helperText={t(RESOURCES.Fields.SoftPosCountHelperText)}
            type="number"
            min={0}
          />
        )}
        {isTerminalsError && (
          <Alert severity="error">
            {t(FACILITY_TERMINAL_RESOURCES.LoadError)}
          </Alert>
        )}
        {isStockExceeded && (
          <Alert severity="warning">
            {t(TERMINAL_RESOURCES.StockExceeded, {
              total: totalRequiredCount,
              available: totalAvailableStock,
            })}
          </Alert>
        )}
        {isTerminalSelectionVisible && (
          <>
            <Typography variant="body2" fontWeight={500}>
              {t(TERMINAL_RESOURCES.Title)}
            </Typography>
            <TerminalSelectionPanel
              terminals={allTerminals}
              selectedTerminalIDs={selectedTerminalIDs}
              assignedTerminalIDs={assignedTerminalIDs}
              requiredCount={requiredCount}
              isLoading={isLoadingTerminals}
              onSelectionChange={onSelectionChange}
            />
          </>
        )}
      </Stack>
    </FormAccordionSection>
  );
};
